<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import SelectCustomer from 'dashboard/components/dahreply/SelectCustomer.vue';
import AvailabilityPicker from '../components/AvailabilityPicker.vue';
import { reportInvalidForm } from '../formFeedback';
import { FIELD_CLASS, SELECT_CLASS, APPT_STATUSES } from '../constants';
import {
  apiErrorMessage,
  bookingErrorMessage,
} from '../../../../api/realEstate/errors';

const LOCATION_TYPES = ['showroom', 'property', 'virtual', 'office'];

// Appointment Viewings are booked against the property as a whole; Key
// Handovers always need a specific floor plan and a typed duration
// (docs/real-estate/be/PRD.md §4.2 / 10-api-reference.md §A13). There is
// no "Deal Confirmed" gate. Availability (dates/slots/arrival windows) is
// entirely owned by AvailabilityPicker — this modal only assembles the
// rest of the create payload around whatever it emits.
export default {
  name: 'PropertyAppointmentModal',
  components: { SelectCustomer, AvailabilityPicker },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      locationTypes: LOCATION_TYPES,
      apptStatuses: APPT_STATUSES,
      customer: null,
      propertyId: '',
      appointmentType: 'viewing',
      unitId: '',
      durationMinutes: null,
      locationType: 'showroom',
      status: 'requested',
      locationNote: '',
      customerNotes: '',
      agentNotes: '',
      handoverChecklist: {
        payment_proof_received: false,
        signed_agreement_on_file: false,
      },
      availability: null,
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', {
      projects: 'getProjectOptions',
      getProject: 'getProject',
    }),
    project() {
      return this.propertyId ? this.getProject(this.propertyId) : null;
    },
    bookingMode() {
      return this.project ? this.project.booking_mode : 'walk_in';
    },
    isKeyHandover() {
      return this.appointmentType === 'key_handover';
    },
    isWalkInBooking() {
      return this.bookingMode === 'walk_in' && !this.isKeyHandover;
    },
    fieldErrors() {
      return {
        customer: !this.customer,
        property: !this.propertyId,
        availability: !this.availability,
        unit: this.isKeyHandover && !this.unitId,
        duration: this.isKeyHandover && !this.durationMinutes,
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  methods: {
    ...mapActions('realEstate', ['fetchProject']),
    ...mapActions('realEstateAppointments', ['addAppointment']),
    handleCustomerSelect(customer) {
      this.customer = customer;
    },
    async handleProjectChange() {
      this.unitId = '';
      this.availability = null;
      if (!this.propertyId) return;
      try {
        await this.fetchProject(this.propertyId);
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    handleTypeChange() {
      this.unitId = '';
      this.durationMinutes = null;
      this.availability = null;
      this.handoverChecklist = {
        payment_proof_received: false,
        signed_agreement_on_file: false,
      };
    },
    handleDurationChange(event) {
      this.durationMinutes = Number(event.target.value) || null;
    },
    handleAvailabilityChange(value) {
      this.availability = value;
    },
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        const fields = {
          appointment_type: this.appointmentType,
          property_id: this.propertyId,
          customer_id: this.customer.id,
          date: this.availability.date,
          start_time: this.availability.start_time,
          location_type: this.locationType,
          status: this.status,
          location_note: this.locationNote || null,
          customer_notes: this.customerNotes || null,
          agent_notes: this.agentNotes || null,
        };
        if (this.customer.conversations && this.customer.conversations.length) {
          fields.conversation_id = this.customer.conversations[0].display_id;
        }
        if (this.unitId) fields.unit_id = this.unitId;
        if (this.availability.agent_id) {
          fields.agent_id = this.availability.agent_id;
        } else if (this.availability.auto_assign_agent) {
          fields.auto_assign_agent = true;
        }
        if (this.isKeyHandover) {
          fields.duration_minutes = this.durationMinutes;
          fields.handover_checklist = { ...this.handoverChecklist };
        }
        await this.addAppointment(fields);
        useAlert(this.$t('REAL_ESTATE.APPOINTMENTS.SUCCESS_CREATED'));
        this.$emit('close', { created: true });
      } catch (err) {
        useAlert(
          bookingErrorMessage(this, err, {
            isWalkIn: this.isWalkInBooking,
            fallbackKey: 'REAL_ESTATE.APPOINTMENTS.ERROR_CREATE',
          })
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.APPOINTMENTS.MODAL_ADD_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div>
          <SelectCustomer
            required
            :invalid="submitted && fieldErrors.customer"
            @select-customer="handleCustomerSelect"
          />
          <p
            v-if="submitted && fieldErrors.customer"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_PROPERTY') }}
              <span class="text-red-500">*</span>
            </label>
            <select
              v-model="propertyId"
              :class="[
                selectClass,
                {
                  '!border-red-500': submitted && fieldErrors.property,
                },
              ]"
              required
              @change="handleProjectChange"
            >
              <option value="" disabled>
                {{ $t('REAL_ESTATE.APPOINTMENTS.SELECT_PROPERTY_PLACEHOLDER') }}
              </option>
              <option
                v-for="proj in projects"
                :key="proj.property_id"
                :value="proj.property_id"
              >
                {{ proj.name }}
              </option>
            </select>
            <p
              v-if="submitted && fieldErrors.property"
              class="mt-1 mb-0 text-xs text-red-500"
            >
              {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
            </p>
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_TYPE') }}
            </label>
            <select
              v-model="appointmentType"
              :class="selectClass"
              @change="handleTypeChange"
            >
              <option value="viewing">
                {{ $t('REAL_ESTATE.APPOINTMENTS.TYPE_VIEWING') }}
              </option>
              <option value="key_handover">
                {{ $t('REAL_ESTATE.APPOINTMENTS.TYPE_KEY_HANDOVER') }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="project">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label
                class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_FLOOR_PLAN') }}
                <span v-if="isKeyHandover" class="text-red-500">*</span>
              </label>
              <select
                v-model="unitId"
                :class="[
                  selectClass,
                  {
                    '!border-red-500': submitted && fieldErrors.unit,
                  },
                ]"
                :required="isKeyHandover"
              >
                <option value="">
                  {{
                    $t('REAL_ESTATE.APPOINTMENTS.SELECT_FLOOR_PLAN_PLACEHOLDER')
                  }}
                </option>
                <option
                  v-for="unit in project.units"
                  :key="unit.unit_id"
                  :value="unit.unit_id"
                >
                  {{ unit.floor_plan_code }}
                </option>
              </select>
              <p
                v-if="submitted && fieldErrors.unit"
                class="mt-1 mb-0 text-xs text-red-500"
              >
                {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
              </p>
            </div>
            <div v-if="isKeyHandover">
              <label
                class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_DURATION') }}
                <span class="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                step="5"
                :value="durationMinutes"
                :class="[
                  fieldClass,
                  {
                    '!border-red-500': submitted && fieldErrors.duration,
                  },
                ]"
                required
                @change="handleDurationChange"
              />
              <p
                v-if="submitted && fieldErrors.duration"
                class="mt-1 mb-0 text-xs text-red-500"
              >
                {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
              </p>
            </div>
          </div>

          <div class="mt-4">
            <p class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
              {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_AVAILABILITY') }}
              <span class="text-red-500">*</span>
            </p>
            <AvailabilityPicker
              :key="`${propertyId}-${appointmentType}`"
              :property-id="propertyId"
              :appointment-type="appointmentType"
              :booking-mode="bookingMode"
              :duration-minutes="isKeyHandover ? durationMinutes : null"
              :agents="project.agents"
              @change="handleAvailabilityChange"
            />
            <p
              v-if="submitted && fieldErrors.availability"
              class="mt-1 mb-0 text-xs text-red-500"
            >
              {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
            </p>
          </div>
        </div>

        <div
          v-if="isKeyHandover"
          class="border-t border-slate-200 dark:border-slate-700 pt-3"
        >
          <p
            class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_TITLE') }}
          </p>
          <label
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-1"
          >
            <input
              v-model="handoverChecklist.payment_proof_received"
              type="checkbox"
            />
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_PAYMENT') }}
          </label>
          <label
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
          >
            <input
              v-model="handoverChecklist.signed_agreement_on_file"
              type="checkbox"
            />
            {{ $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_AGREEMENT') }}
          </label>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_LOCATION_TYPE') }}
          </label>
          <select v-model="locationType" :class="selectClass">
            <option v-for="type in locationTypes" :key="type" :value="type">
              {{
                $t(
                  `REAL_ESTATE.APPOINTMENTS.LOCATION_TYPE_${type.toUpperCase()}`
                )
              }}
            </option>
          </select>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_STATUS') }}
          </label>
          <select v-model="status" :class="selectClass">
            <option
              v-for="(label, key) in apptStatuses"
              :key="key"
              :value="key"
            >
              {{ label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_CUSTOMER_NOTES') }}
          </label>
          <textarea
            v-model="customerNotes"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_CUSTOMER_NOTES_PLACEHOLDER')
            "
            rows="2"
            :class="fieldClass"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_AGENT_NOTES') }}
          </label>
          <textarea
            v-model="agentNotes"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_AGENT_NOTES_PLACEHOLDER')
            "
            rows="2"
            :class="fieldClass"
          />
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.APPOINTMENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.APPOINTMENTS.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
